import React, { useState, useEffect, useCallback } from 'react';

// Configuración de la API - Detectar si estamos en app nativa (Capacitor)
const isNative = window.Capacitor?.isNativePlatform?.() || false;
const API_BASE_URL = isNative 
  ? 'https://api.appagustinos.es/api'  // App nativa - URL directa al servidor
  : (process.env.REACT_APP_API_URL || '/api');  // PWA - Proxy de Vercel

// Constantes de cache (fuera del componente para evitar recreación)
const CACHE_KEYS = {
  SANTORAL: 'calendar_santoral_cache',
  SANTOS: 'calendar_santos_cache',
  TE_INTERESA: 'calendar_te_interesa_cache',
  TIMESTAMP: 'calendar_cache_timestamp'
};

const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 horas en milisegundos

// Formatea una fecha como YYYY-MM-DD usando la hora LOCAL del navegador.
// Evita el bug de toISOString() que convierte a UTC y desplaza el día en
// zonas horarias con offset positivo (Europa/Madrid en verano, UTC+2).
const toLocalISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const FullCalendar = ({ onClose }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayEvents, setDayEvents] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [loadingSantos, setLoadingSantos] = useState(true);
  const [loadingEventos, setLoadingEventos] = useState(true);
  const [loadingSantoral, setLoadingSantoral] = useState(true);
  const [eventosSantos, setEventosSantos] = useState([]);
  const [eventosTeInteresa, setEventosTeInteresa] = useState([]);
  const [eventosSantoral, setEventosSantoral] = useState([]);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const days = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

  // ===== FUNCIONES DE CACHE =====
  // Verificar si el cache ha expirado
  const isCacheExpired = () => {
    try {
      const timestamp = localStorage.getItem(CACHE_KEYS.TIMESTAMP);
      if (!timestamp) return true;
      
      const now = Date.now();
      const cacheAge = now - parseInt(timestamp);
      return cacheAge > CACHE_DURATION;
    } catch (error) {
      console.warn('⚠️ Error verificando expiración del cache:', error);
      return true;
    }
  };

  // Guardar datos en cache
  const saveToCache = (key, data) => {
    try {
      localStorage.setItem(key, JSON.stringify(data));
      console.log(`💾 Cache guardado: ${key} (${data.length} items)`);
    } catch (error) {
      console.warn(`⚠️ Error guardando cache ${key}:`, error);
    }
  };

  // Cargar datos desde cache
  const loadFromCache = (key) => {
    try {
      const cached = localStorage.getItem(key);
      if (cached) {
        const data = JSON.parse(cached);
        console.log(`📦 Cache cargado: ${key} (${data.length} items)`);
        return data;
      }
      return null;
    } catch (error) {
      console.warn(`⚠️ Error cargando cache ${key}:`, error);
      return null;
    }
  };

  // Actualizar timestamp del cache
  const updateCacheTimestamp = () => {
    try {
      localStorage.setItem(CACHE_KEYS.TIMESTAMP, Date.now().toString());
      setLastSyncTime(new Date());
    } catch (error) {
      console.warn('⚠️ Error actualizando timestamp:', error);
    }
  };

  // Obtener tiempo relativo desde última sincronización
  const getTimeSinceSync = () => {
    try {
      const timestamp = localStorage.getItem(CACHE_KEYS.TIMESTAMP);
      if (!timestamp) return null;
      
      const minutes = Math.floor((Date.now() - parseInt(timestamp)) / 60000);
      if (minutes < 60) return `hace ${minutes} min`;
      
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `hace ${hours}h`;
      
      const days = Math.floor(hours / 24);
      return `hace ${days}d`;
    } catch (error) {
      return null;
    }
  };

  // Función para cargar santos agustinos desde la base de datos
  const cargarSantosAgustinos = useCallback(async (targetYear = null, forceReload = false) => {
    try {
      console.log('📄 Cargando santos agustinos desde la base de datos...');
      setLoadingSantos(true);

      // 1. Verificar cache en memoria
      if (!forceReload && window.agustinosAppData?.cachedCalendarEvents?.santos) {
        console.log('⚡ Usando santos desde cache en memoria');
        setEventosSantos(window.agustinosAppData.cachedCalendarEvents.santos);
        setLoadingSantos(false);
        return;
      }

      // 2. Verificar cache en localStorage
      if (!forceReload && !isCacheExpired()) {
        const cached = loadFromCache(CACHE_KEYS.SANTOS);
        if (cached && cached.length > 0) {
          console.log('📦 Usando santos desde localStorage');
          setEventosSantos(cached);
          
          // Guardar en memoria para próximas aperturas
          if (!window.agustinosAppData.cachedCalendarEvents) {
            window.agustinosAppData.cachedCalendarEvents = {};
          }
          window.agustinosAppData.cachedCalendarEvents.santos = cached;
          
          setLoadingSantos(false);
          return;
        }
      }

      // 3. Cargar desde la base de datos (cache expiró o forzado)
      console.log('🔄 Cargando santos desde la base de datos...');
      const appData = window.agustinosAppData;
      if (!appData || !appData.sections || !appData.sections['Santos Agustinos']) {
        console.warn('⚠️ Santos Agustinos no disponibles en appData');
        setLoadingSantos(false);
        return;
      }

      let santos = appData.sections['Santos Agustinos'];
      console.log(`📄 FullCalendar - Usando ${santos.length} santos desde DataLoader`);
      
      if (santos.length < 30 && appData.loader && appData.loader.cargarSeccionCompleta) {
        console.log('📄 FullCalendar - Pocos santos cargados, intentando cargar sección completa...');
        try {
          const santosCompletos = await appData.loader.cargarSeccionCompleta('Santos Agustinos', 500);
          if (santosCompletos.length > santos.length) {
            santos = santosCompletos;
            console.log(`✅ FullCalendar - Sección completa cargada: ${santos.length} santos`);
            appData.sections['Santos Agustinos'] = santos;
          }
        } catch (error) {
          console.warn('⚠️ FullCalendar - Error cargando sección completa, usando datos parciales:', error);
        }
      }
      
      // Agrupar santos por día/mes y quedarse solo con el más reciente
      const santosPorFecha = new Map();
      
      santos.forEach(santo => {
        const fechaPublicacion = new Date(santo.date);
        const mes = fechaPublicacion.getMonth();
        const dia = fechaPublicacion.getDate();
        const clave = `${mes}-${dia}`;
        
        if (!santosPorFecha.has(clave)) {
          santosPorFecha.set(clave, santo);
        } else {
          const santoExistente = santosPorFecha.get(clave);
          const fechaExistente = new Date(santoExistente.date);
          
          if (fechaPublicacion > fechaExistente) {
            santosPorFecha.set(clave, santo);
          }
        }
      });
      
      // Convertir santos a eventos de calendario (múltiples años)
      const eventosSantos = [];
      const baseYear = targetYear || new Date().getFullYear();
      const yearsToGenerate = [baseYear - 2, baseYear - 1, baseYear, baseYear + 1, baseYear + 2];
      
      santosPorFecha.forEach(santo => {
        const fechaPublicacion = new Date(santo.date);
        const mes = fechaPublicacion.getMonth();
        const dia = fechaPublicacion.getDate();
        
        yearsToGenerate.forEach(year => {
          const fechaFiesta = new Date(year, mes, dia);
          
          eventosSantos.push({
            eventName: santo.title,
            calendar: 'Santos Agustinos',
            color: 'red',
            date: toLocalISODate(fechaFiesta),
            articleId: santo.id,
            articleData: santo,
            url: `/santos-agustinos/i/${santo.id}/${santo.title.toLowerCase().replace(/\s+/g, '-')}`,
            year: year
          });
        });
      });

      setEventosSantos(eventosSantos);
      
      // Guardar en cache (memoria + localStorage)
      if (!window.agustinosAppData.cachedCalendarEvents) {
        window.agustinosAppData.cachedCalendarEvents = {};
      }
      window.agustinosAppData.cachedCalendarEvents.santos = eventosSantos;
      saveToCache(CACHE_KEYS.SANTOS, eventosSantos);
      
      console.log(`📅 Santos cargados y cacheados: ${eventosSantos.length} eventos para ${yearsToGenerate.length} años`);

    } catch (error) {
      console.error('❌ Error cargando santos agustinos:', error);
    } finally {
      setLoadingSantos(false);
    }
  }, []);

  // Función para cargar eventos de "Te interesa"
  const cargarEventosTeInteresa = useCallback(async (targetYear = null, forceReload = false) => {
    try {
      console.log('📅 Cargando eventos de "Te interesa" desde la base de datos...');
      setLoadingEventos(true);

      // 1. Verificar cache en memoria
      if (!forceReload && window.agustinosAppData?.cachedCalendarEvents?.teInteresa) {
        console.log('⚡ Usando Te interesa desde cache en memoria');
        setEventosTeInteresa(window.agustinosAppData.cachedCalendarEvents.teInteresa);
        setLoadingEventos(false);
        return;
      }

      // 2. Verificar cache en localStorage
      if (!forceReload && !isCacheExpired()) {
        const cached = loadFromCache(CACHE_KEYS.TE_INTERESA);
        if (cached && cached.length > 0) {
          console.log('📦 Usando Te interesa desde localStorage');
          setEventosTeInteresa(cached);
          
          // Guardar en memoria
          if (!window.agustinosAppData.cachedCalendarEvents) {
            window.agustinosAppData.cachedCalendarEvents = {};
          }
          window.agustinosAppData.cachedCalendarEvents.teInteresa = cached;
          
          setLoadingEventos(false);
          return;
        }
      }

      // 3. Cargar desde la base de datos
      console.log('🔄 Cargando Te interesa desde la base de datos...');
      const appData = window.agustinosAppData;
      if (!appData || !appData.sections || !appData.sections['Te interesa']) {
        console.warn('⚠️ Te interesa no disponibles en appData');
        setLoadingEventos(false);
        return;
      }

      let eventos = appData.sections['Te interesa'];
      console.log(`📄 FullCalendar - Usando ${eventos.length} eventos desde DataLoader`);
      
      if (eventos.length < 30 && appData.loader && appData.loader.cargarSeccionCompleta) {
        console.log('📄 FullCalendar - Pocos eventos cargados, intentando cargar sección completa...');
        try {
          const eventosCompletos = await appData.loader.cargarSeccionCompleta('Te interesa', 500);
          if (eventosCompletos.length > eventos.length) {
            eventos = eventosCompletos;
            console.log(`✅ FullCalendar - Sección completa cargada: ${eventos.length} eventos`);
            appData.sections['Te interesa'] = eventos;
          }
        } catch (error) {
          console.warn('⚠️ FullCalendar - Error cargando sección completa, usando datos parciales:', error);
        }
      }
      
      // Agrupar eventos por día/mes y quedarse solo con el más reciente
      const eventosPorFecha = new Map();
      
      eventos.forEach(evento => {
        const fechaPublicacion = new Date(evento.date);
        const mes = fechaPublicacion.getMonth();
        const dia = fechaPublicacion.getDate();
        const clave = `${mes}-${dia}`;
        
        if (!eventosPorFecha.has(clave)) {
          eventosPorFecha.set(clave, evento);
        } else {
          const eventoExistente = eventosPorFecha.get(clave);
          const fechaExistente = new Date(eventoExistente.date);
          
          if (fechaPublicacion > fechaExistente) {
            eventosPorFecha.set(clave, evento);
          }
        }
      });
      
      // Convertir eventos a eventos de calendario (múltiples años)
      const eventosCalendario = [];
      const baseYear = targetYear || new Date().getFullYear();
      const yearsToGenerate = [baseYear - 2, baseYear - 1, baseYear, baseYear + 1, baseYear + 2];
      
      eventosPorFecha.forEach(evento => {
        const fechaPublicacion = new Date(evento.date);
        const mes = fechaPublicacion.getMonth();
        const dia = fechaPublicacion.getDate();
        
        yearsToGenerate.forEach(year => {
          const fechaEvento = new Date(year, mes, dia);
          
          eventosCalendario.push({
            eventName: evento.title,
            calendar: 'Te interesa',
            color: 'green',
            date: toLocalISODate(fechaEvento),
            articleId: evento.id,
            articleData: evento,
            url: `/te-interesa/i/${evento.id}/${evento.title.toLowerCase().replace(/\s+/g, '-')}`,
            year: year
          });
        });
      });

      setEventosTeInteresa(eventosCalendario);

      // Guardar en cache (memoria + localStorage)
      if (!window.agustinosAppData.cachedCalendarEvents) {
        window.agustinosAppData.cachedCalendarEvents = {};
      }
      window.agustinosAppData.cachedCalendarEvents.teInteresa = eventosCalendario;
      saveToCache(CACHE_KEYS.TE_INTERESA, eventosCalendario);

      console.log(`📅 Te interesa cargados y cacheados: ${eventosCalendario.length} eventos para ${yearsToGenerate.length} años`);

    } catch (error) {
      console.error('❌ Error cargando eventos de Te interesa:', error);
    } finally {
      setLoadingEventos(false);
    }
  }, []);

  // NUEVA FUNCIÓN: Cargar santoral y días internacionales desde backend
  const cargarEventosBackend = useCallback(async (targetYear = null, forceReload = false) => {
    try {
      console.log('🔵 Cargando santoral y eventos desde backend...');
      setLoadingSantoral(true);

      // 1. Verificar cache en memoria
      if (!forceReload && window.agustinosAppData?.cachedCalendarEvents?.santoral) {
        console.log('⚡ Usando santoral desde cache en memoria');
        setEventosSantoral(window.agustinosAppData.cachedCalendarEvents.santoral);
        setLoadingSantoral(false);
        return;
      }

      // 2. Verificar cache en localStorage
      if (!forceReload && !isCacheExpired()) {
        const cached = loadFromCache(CACHE_KEYS.SANTORAL);
        if (cached && cached.length > 0) {
          console.log('📦 Usando santoral desde localStorage');
          setEventosSantoral(cached);
          
          // Guardar en memoria
          if (!window.agustinosAppData.cachedCalendarEvents) {
            window.agustinosAppData.cachedCalendarEvents = {};
          }
          window.agustinosAppData.cachedCalendarEvents.santoral = cached;
          
          setLoadingSantoral(false);
          return;
        }
      }

      // 3. Cargar desde backend (cache expiró o forzado)
      console.log('🔄 Cargando santoral desde backend API...');
      const baseYear = targetYear || new Date().getFullYear();
      const yearsToGenerate = [baseYear - 2, baseYear - 1, baseYear, baseYear + 1, baseYear + 2];
      
      const allEvents = [];

      // Cargar eventos para cada año
      for (const year of yearsToGenerate) {
        for (let month = 1; month <= 12; month++) {
          try {
            const response = await fetch(`${API_BASE_URL}/calendar/events/month/${year}/${month}`);
            
            if (!response.ok) {
              console.warn(`⚠️ No se pudieron cargar eventos de ${year}-${month}`);
              continue;
            }

            const data = await response.json();
            
            if (data.success && data.data.events) {
              data.data.events.forEach(event => {
                allEvents.push({
                  eventName: event.title,
                  calendar: event.event_type === 'santoral' ? 'Santoral' : 'Días internacionales',
                  color: event.color,
                  date: event.date.split('T')[0],
                  eventType: event.event_type,
                  articleId: event.article_id,
                  description: event.description,
                  year: year
                });
              });
            }
          } catch (error) {
            console.warn(`⚠️ Error cargando ${year}-${month}:`, error.message);
          }
        }
      }

      setEventosSantoral(allEvents);
      
      // Guardar en cache (memoria + localStorage)
      if (!window.agustinosAppData.cachedCalendarEvents) {
        window.agustinosAppData.cachedCalendarEvents = {};
      }
      window.agustinosAppData.cachedCalendarEvents.santoral = allEvents;
      saveToCache(CACHE_KEYS.SANTORAL, allEvents);
      
      // Actualizar timestamp solo después de cargar todos los datos
      updateCacheTimestamp();
      
      // Contar tipos de eventos
      const santoralCount = allEvents.filter(e => e.eventType === 'santoral').length;
      const eventosCount = allEvents.filter(e => e.eventType === 'evento').length;
      console.log(`🔵 Santoral cargado y cacheado: ${allEvents.length} eventos totales`);
      console.log(`   📘 Santoral: ${santoralCount} eventos`);
      console.log(`   🎉 Días internacionales: ${eventosCount} eventos`);

    } catch (error) {
      console.error('❌ Error cargando eventos del backend:', error);
    } finally {
      setLoadingSantoral(false);
    }
  }, []);

  // Función para forzar sincronización manual
  const forzarSincronizacion = useCallback(async () => {
    if (isRefreshing) return;
    
    console.log('🔄 Forzando sincronización manual...');
    setIsRefreshing(true);
    
    try {
      await Promise.all([
        cargarSantosAgustinos(null, true),
        cargarEventosTeInteresa(null, true),
        cargarEventosBackend(null, true)
      ]);
      console.log('✅ Sincronización manual completada');
    } catch (error) {
      console.error('❌ Error en sincronización manual:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, cargarSantosAgustinos, cargarEventosTeInteresa, cargarEventosBackend]);

  // Cargar todos los eventos al montar el componente
  useEffect(() => {
    // Cargar timestamp de última sincronización
    const timestamp = localStorage.getItem(CACHE_KEYS.TIMESTAMP);
    if (timestamp) {
      setLastSyncTime(new Date(parseInt(timestamp)));
    }
    
    // Cargar eventos (usará cache si está disponible)
    cargarSantosAgustinos();
    cargarEventosTeInteresa();
    cargarEventosBackend();
  }, [cargarSantosAgustinos, cargarEventosTeInteresa, cargarEventosBackend]);

  // Combinar todos los eventos cuando cambien
  useEffect(() => {
    // Crear un mapa para evitar duplicados de días internacionales
    const eventosMap = new Map();
    
    // 1. Primero agregar eventos de "Te interesa" (artículos - prioridad)
    eventosTeInteresa.forEach(evento => {
      eventosMap.set(evento.date, evento);
    });
    
    // 2. Agregar días internacionales del backend SOLO si no hay artículo
    eventosSantoral
      .filter(evento => evento.eventType === 'evento')
      .forEach(evento => {
        if (!eventosMap.has(evento.date)) {
          // No hay artículo, agregar evento informativo (no clickeable)
          eventosMap.set(evento.date, evento);
        }
      });
    
    // 3. Agregar santoral (azul) - siempre se muestran
    const santoralEvents = eventosSantoral.filter(evento => evento.eventType === 'santoral');
    
    // 4. Agregar santos agustinos (rojo) - siempre se muestran
    const todosLosEventos = [
      ...Array.from(eventosMap.values()), // Eventos verdes (Te interesa + días internacionales sin artículo)
      ...eventosSantos,                    // Santos rojos
      ...santoralEvents                    // Santoral azul
    ];
    
    setCalendarEvents(todosLosEventos);
    console.log(`📅 Calendario combinado: ${todosLosEventos.length} eventos totales`);
  }, [eventosSantos, eventosTeInteresa, eventosSantoral]);

  // Obtener eventos de un día específico
  const getEventsForDay = (date) => {
    const dateString = toLocalISODate(date);
    return calendarEvents.filter(event => event.date === dateString);
  };

  // Generar días del mes
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    
    const dayOfWeek = (firstDay.getDay() + 6) % 7;
    startDate.setDate(firstDay.getDate() - dayOfWeek);
    
    const days = [];
    const currentDay = new Date(startDate);
    
    for (let i = 0; i < 42; i++) {
      const events = getEventsForDay(currentDay);
      days.push({
        date: new Date(currentDay),
        isCurrentMonth: currentDay.getMonth() === month,
        isToday: currentDay.toDateString() === new Date().toDateString(),
        events: events
      });
      currentDay.setDate(currentDay.getDate() + 1);
    }
    
    return days;
  };

  const handleDayClick = (day) => {
    setSelectedDay(day);
    setDayEvents(day.events);
  };

  // Función para manejar clics en eventos
  const handleEventClick = (event) => {
    // Solo abrir si tiene artículo asociado (articleId)
    if (event.articleId && event.articleData) {
      console.log(`🎯 Abriendo artículo: ${event.eventName}`);
      
      const appData = window.agustinosAppData;
      if (appData && appData.loader) {
        const articleClickEvent = new CustomEvent('openSantoArticle', {
          detail: {
            article: event.articleData,
            section: event.calendar === 'Santos Agustinos' ? 'Santos Agustinos' : 'Te interesa'
          }
        });
        window.dispatchEvent(articleClickEvent);
        
        setTimeout(() => {
          if (onClose) onClose();
        }, 100);
      } else if (event.url) {
        window.open(event.url, '_blank');
      }
    } else {
      // Evento informativo sin artículo - no hace nada
      console.log(`ℹ️ Evento informativo (no clickeable): ${event.eventName}`);
    }
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
    setSelectedDay(null);
    setDayEvents([]);
    
    const newYear = newDate.getFullYear();
    const currentYear = new Date().getFullYear();
    
    // Solo recargar si navegamos más allá del rango cacheado Y el cache ha expirado
    if (Math.abs(newYear - currentYear) > 2 && isCacheExpired()) {
      console.log(`📅 Navegando a ${newYear} y cache expirado, recargando eventos...`);
      cargarSantosAgustinos(newYear, false);
      cargarEventosTeInteresa(newYear, false);
      cargarEventosBackend(newYear, false);
    }
  };

  const calendarDays = generateCalendarDays();

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
      style={{
        zIndex: 1200,
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)'
      }}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 flex flex-col overflow-hidden"
        style={{
          maxHeight: 'calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 16px)',
          minHeight: 0
        }}
      >
        {/* Header del popup */}
        <div className="bg-white p-4 border-b flex-shrink-0">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-xl font-light tracking-wide">
              Calendario
            </h2>
            <div className="flex items-center space-x-2">
              {/* Botón de sincronización */}
              <button 
                onClick={forzarSincronizacion}
                disabled={isRefreshing}
                className={`text-sm px-3 py-1 rounded-lg transition-all ${
                  isRefreshing 
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                    : 'bg-red-50 text-red-600 hover:bg-red-100'
                }`}
                title="Actualizar calendario"
                style={isRefreshing ? {
                  animation: 'spin 1s linear infinite'
                } : {}}
              >
                {isRefreshing ? '⟳' : '↻'}
              </button>
              <style>{`
                @keyframes spin {
                  from { transform: rotate(0deg); }
                  to { transform: rotate(360deg); }
                }
              `}</style>
              <button 
                onClick={onClose}
                className="text-gray-500 hover:text-black text-xl"
              >
                ✕
              </button>
            </div>
          </div>
          {/* Indicador de última sincronización */}
          {lastSyncTime && (
            <div className="text-xs text-gray-500 text-center">
              Última actualización: {getTimeSinceSync()}
            </div>
          )}
        </div>

        {/* Navegación del mes */}
        <div className="p-4 border-b flex justify-center items-center flex-shrink-0">
          <button
            onClick={() => navigateMonth(-1)}
            className="w-0 h-0 border-r-[10px] border-r-gray-400 border-t-[7.5px] border-t-transparent border-b-[7.5px] border-b-transparent hover:border-r-black mr-6"
          />
          <h3 className="text-lg font-light tracking-wide px-4">
            {months[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h3>
          <button
            onClick={() => navigateMonth(1)}
            className="w-0 h-0 border-l-[10px] border-l-gray-400 border-t-[7.5px] border-t-transparent border-b-[7.5px] border-b-transparent hover:border-l-black ml-6"
          />
        </div>

        <div
          className="p-4"
          style={{
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain'
          }}
        >
          {/* Días de la semana */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {days.map(day => (
              <div key={day} className="text-center text-xs text-red-600 font-medium py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendario */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((day, index) => (
              <div
                key={index}
                onClick={() => handleDayClick(day)}
                className={`
                  text-center p-2 cursor-pointer relative
                  ${day.isCurrentMonth ? 'text-black' : 'text-gray-300'}
                  ${day.isToday ? 'text-red-600' : ''}
                  hover:bg-gray-100
                `}
              >
                <div className={`text-lg ${day.isToday ? 'font-bold' : 'font-light'}`}>
                  {day.date.getDate()}
                </div>
                
                {/* Indicadores de eventos */}
                <div className="flex justify-center space-x-1 mt-1">
                  {day.events.slice(0, 3).map((event, i) => (
                    <div
                      key={i}
                      className={`w-1 h-1 rounded-full ${
                        event.color === 'green' ? 'bg-green-500' : 
                        event.color === 'red' ? 'bg-red-500' : 
                        'bg-blue-500'
                      }`}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Detalles del día seleccionado */}
          {selectedDay && (
            <div className="mt-4 p-3 bg-gray-50 rounded border">
              <div className="text-sm font-medium mb-2">
                {selectedDay.date.toLocaleDateString('es-ES', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </div>
              
              {dayEvents.length > 0 ? (
                <div className="space-y-2">
                  {dayEvents.map((event, index) => (
                    <div 
                      key={index} 
                      className={`flex items-start space-x-2 p-2 rounded-lg transition-colors ${
                        event.articleId ? 'cursor-pointer hover:bg-gray-100' : 'cursor-default'
                      }`}
                      onClick={() => handleEventClick(event)}
                    >
                      <div className={`w-2 h-2 rounded-full mt-1 ${
                        event.color === 'green' ? 'bg-green-500' : 
                        event.color === 'red' ? 'bg-red-500' : 
                        'bg-blue-500'
                      }`} />
                      <div className="text-sm">
                        <div className={`${event.articleId ? 'text-red-600 font-medium hover:underline' : 'text-gray-700'}`}>
                          {event.eventName}
                        </div>
                        {event.articleId && (
                          <div className="text-xs text-gray-500 mt-1">
                            📖 Leer artículo completo
                          </div>
                        )}
                        {!event.articleId && event.description && (
                          <div className="text-xs text-gray-500 mt-1">
                            {event.description}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500">Sin eventos</div>
              )}
            </div>
          )}

          {/* Leyenda */}
          <div className="mt-4 flex justify-center space-x-6 text-xs">
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-green-500 rounded-full" />
              <span>Días internacionales</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-red-500 rounded-full" />
              <span>Santos Agustinos</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-blue-500 rounded-full" />
              <span>Santoral</span>
            </div>
            {(loadingSantos || loadingEventos || loadingSantoral) && (
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse" />
                <span className="text-gray-500">Cargando...</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FullCalendar;